import { SlidersHorizontal, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useLang } from "@/lib/i18n";
import { fmtUSD } from "@/lib/api";
import type { Yacht } from "@/types";

export interface Filters { type: string; cap: number; max: number; sort: string }
export const DEFAULT_FILTERS: Filters = { type: "", cap: 0, max: 0, sort: "" };
const TYPES = ["sport", "motor", "catamaran", "flybridge", "superyacht"];
const CAPS = [0, 6, 10, 15, 20, 30];

export const fromPrice = (y: Yacht) => Math.min(...Object.values(y.prices).filter((p): p is number => typeof p === "number" && p > 0));

export function applyFilters(list: Yacht[], f: Filters): Yacht[] {
  let out = list.filter((y) => (!f.type || y.type === f.type) && (!f.cap || y.capacity >= f.cap) && (!f.max || fromPrice(y) <= f.max));
  if (f.sort === "price_asc") out = [...out].sort((a, b) => fromPrice(a) - fromPrice(b));
  if (f.sort === "price_desc") out = [...out].sort((a, b) => fromPrice(b) - fromPrice(a));
  if (f.sort === "size") out = [...out].sort((a, b) => b.length_ft - a.length_ft);
  if (f.sort === "rating") out = [...out].sort((a, b) => (b.rating?.avg || 0) - (a.rating?.avg || 0));
  return out;
}

interface Props { all: Yacht[]; value: Filters; onChange: (f: Filters) => void }

export default function FleetFilters({ all, value, onChange }: Props) {
  const { t } = useLang();
  const prices = all.map(fromPrice).filter(Number.isFinite);
  const lo = prices.length ? Math.min(...prices) : 0;
  const hi = prices.length ? Math.max(...prices) : 0;
  const cur = value.max || hi;
  const active = !!(value.type || value.cap || value.max || value.sort);
  const set = (p: Partial<Filters>) => onChange({ ...value, ...p });
  const chip = (on: boolean) => `rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`;

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white/70 backdrop-blur p-5 sm:p-6" data-testid="fleet-filters">
      <div className="flex items-center justify-between gap-3">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><SlidersHorizontal className="h-4 w-4" />{t("flt.title")}</p>
        {active && <button onClick={() => onChange(DEFAULT_FILTERS)} data-testid="fleet-filters-clear" className="inline-flex items-center gap-1 text-xs font-semibold text-[#008C86] hover:underline"><X className="h-3.5 w-3.5" />{t("flt.clear")}</button>}
      </div>
      <div className="mt-5 grid gap-6 lg:grid-cols-[1.6fr_1fr_1fr_auto]">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">{t("flt.type")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={() => set({ type: "" })} data-testid="flt-type-all" className={chip(!value.type)}>{t("flt.all")}</button>
            {TYPES.map((k) => <button key={k} onClick={() => set({ type: value.type === k ? "" : k })} data-testid={`flt-type-${k}`} className={chip(value.type === k)}>{t(`type.${k}`)}</button>)}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">{t("flt.cap")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {CAPS.map((c) => <button key={c} onClick={() => set({ cap: c })} data-testid={`flt-cap-${c}`} className={chip(value.cap === c)}>{c === 0 ? t("flt.any") : `${c}+`}</button>)}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">{t("flt.price")} <span className="text-[#0B192C] font-semibold normal-case tracking-normal" data-testid="flt-price-value">≤ {fmtUSD(cur)}</span></p>
          <div className="mt-4 px-1">
            <Slider min={lo} max={hi} step={50} value={[cur]} onValueChange={([v]) => set({ max: v >= hi ? 0 : v })} data-testid="flt-price-slider" />
            <div className="mt-2 flex justify-between text-[11px] text-slate-400"><span>{fmtUSD(lo)}</span><span>{fmtUSD(hi)}</span></div>
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">{t("flt.sort")}</p>
          <select value={value.sort} onChange={(e) => set({ sort: e.target.value })} data-testid="flt-sort" className="mt-2 w-full rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700">
            <option value="">{t("flt.sortDefault")}</option>
            <option value="price_asc">{t("flt.sortPriceAsc")}</option>
            <option value="price_desc">{t("flt.sortPriceDesc")}</option>
            <option value="size">{t("flt.sortSize")}</option>
            <option value="rating">{t("flt.sortRating")}</option>
          </select>
        </div>
      </div>
    </div>
  );
}
